import * as DialogPrimitive from "@radix-ui/react-dialog";
import { MaximizeIcon, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useState,
} from "react";
import { useGlobalContext } from "../context/GlobalProvider";

export type DialogContextType = {
  maximized: boolean;
  setMaximized: (maximized: boolean) => void;
};

export const DialogContext = createContext<DialogContextType>({
  maximized: false,
  setMaximized: () => {},
});

export const useDialog = () => {
  const context = useContext<DialogContextType>(DialogContext);

  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }

  return context;
};

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    disableClose?: boolean;
    overlayClassname?: string;
    dialogId?: string;
    defaultMaximized?: boolean;
  }
>(
  (
    {
      className,
      children,
      disableClose,
      overlayClassname,
      dialogId,
      defaultMaximized = false,
      ...props
    },
    ref,
  ) => {
    const { devtoolsRef } = useGlobalContext();
    const [maximized, setMaximized] = useState(() => {
      if (!dialogId) return defaultMaximized;

      const storageKey = `unideck.dialog.${dialogId}.maximized`;
      const storedValue = localStorage.getItem(storageKey);

      if (storedValue === null) {
        return defaultMaximized;
      }

      try {
        return JSON.parse(storedValue) as boolean;
      } catch {
        return defaultMaximized;
      }
    });

    const updateMaximized = useCallback(
      (maximized: boolean) => {
        setMaximized(maximized);
        if (!dialogId) return;

        localStorage.setItem(
          `unideck.dialog.${dialogId}.maximized`,
          JSON.stringify(maximized),
        );
      },
      [dialogId],
    );

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassname} />
        <DialogPrimitive.Content
          ref={ref}
          /*
          grid-item-no-drag class prevents dragging react-grid-layout items when modal is open
          https://github.com/react-grid-layout/react-grid-layout/issues/869
        */
          className={cn(
            // `w-[calc(100vw-2rem)]` + `max-h-[calc(100dvh-2rem)] overflow-y-auto` keep every
            // dialog inset from the screen edges and inside the viewport on small/short screens
            // (call-site `max-w-*`/`h-*` still win via cn ordering, but never exceed the viewport).
            "grid-item-no-drag fixed left-[50%] top-[50%] z-50 grid w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto bg-background p-6 shadow-lg duration-200 @container/dialog-content data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-xl",
            className,
            {
              "h-screen max-h-full w-screen max-w-[100vw]": maximized,
            },
          )}
          onInteractOutside={ev => {
            if (
              import.meta.env.DEV &&
              devtoolsRef.current?.contains(ev.target as Node)
            ) {
              ev.preventDefault();
            }
          }}
          {...props}
        >
          <DialogContext.Provider
            value={{
              maximized,
              setMaximized: updateMaximized,
            }}
          >
            {children}
          </DialogContext.Provider>
          {disableClose ? null : (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogMaximize = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof Button>
>(({ className, variant = "secondary", size = "icon", ...props }, ref) => {
  const { maximized, setMaximized } = useDialog();

  return (
    <Button
      variant={variant}
      className={cn("rounded-xl", className)}
      size={size}
      ref={ref}
      onClick={() => setMaximized(!maximized)}
      {...props}
    >
      <MaximizeIcon className="size-4" />
      <span className="sr-only">Maximize</span>
    </Button>
  );
});

DialogMaximize.displayName = "DialogMaximize";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
